import React, { useCallback, useEffect, useMemo, useState } from "react";
import AppEngine from "../../apps/AppEngine";
import { APPS, QUERY_AND_SESSION_KEYS, ROUTES, SERVER_SIDE_CACHE_KEYS } from "~/constants";
import styles from "./DevEnvironment.module.scss";
import { fetchBrandNames } from "~/services";
import Input from "~/components/inputs/Input";
import { Checkbox } from "~/components/inputs/CheckBox";
import devBrandData from "~/mocks/devBrandData";
import { getLocalMockBrandData, getLocalMockBrandNames, isBrandLocalMock } from "./DevEnvironment.core";
import SettingsComponent from "../SettingsComponent";
import DemoEnvironment from "../demo/DemoEnvironment";
import _ from "lodash";
import ProdEnvironment from "../prod/ProdEnvironment";
import { ENVIRONMENTS, EnvironmentSelector, getHostSelectOptions, getServerHostKey, getServerHostUrl, HOSTS, isDemoEnvironment, isLocalDev, serviceIsLoading, useServiceHook } from "rylak-common";
import { BROWSER_STORAGE_TYPES, useBrowserSessionAndLocalState } from "rylak-common/src/hooks/useBrowserStateOld.jsx";
import useBrowserStateWithCookieCheck from "~/hooks/useBrowserStateWithCookieCheck";
import { useQueryState } from "rylak-common/src/hooks/useQueryState.jsx";
import { useRouter } from "next/router";
import { IFRAME_ACTIONS, INGRID_IFRAME_ID, INGRID_IFRAME_MODAL_APP_ID } from "rylak-common/src/constants";
import TurnrDropdownSelect from "~/components/inputs/TurnrDropdownSelect";
import { devFetch } from "~/app/environments/dev/devFetch.js";

export const APP_ENVIRONMENT_OPTIONS = [APPS.DEV_APP, APPS.DEMO_APP, APPS.APP];

export default function DevEnvironment(props) {
    const { serverSideProps } = props;
    const { brandName } = serverSideProps;
    const [appEnvironment, setAppEnvironment] = useQueryState({
        key: QUERY_AND_SESSION_KEYS.APP_ENVIRONMENT,
        defaultValue: APP_ENVIRONMENT_OPTIONS[0],
    });
    if (appEnvironment === APPS.APP) {
        return <ProdEnvironment {...props} />;
    }
    if (appEnvironment === APPS.DEMO_APP) {
        return <DemoEnvironment {...props} />;
    }

    return <DevAppInner {...props} key={brandName} brandName={brandName} setAppEnvironment={setAppEnvironment} />;
}

const IFRAME_OPTIONS = {
    NO_IFRAME: {
        value: "NO_IFRAME",
        label: "No Iframe",
    },
    ONE_IFRAME: {
        value: "ONE_IFRAME",
        label: "Single Iframe",
    },
    TWO_IFRAMES: {
        value: "TWO_IFRAMES",
        label: "Single & Global Iframe",
    },
};

function DevAppInner(props) {
    const { setAppEnvironment, serverSideProps, queryClient, Page, urlHost, brandName } = props;
    const { url } = serverSideProps;

    const router = useRouter();

    const serverHostUrl = getServerHostUrl(url);

    const originalSystem = useMemo(
        () => ({
            fetch: fetch,
            host: serverHostUrl,
        }),
        [serverHostUrl],
    );

    const serverHostKey = getServerHostKey(url);
    const includeReturnReasons = isDemoEnvironment() || isLocalDev();

    const [forceRealReturnReasons] = useQueryState({
        key: QUERY_AND_SESSION_KEYS.FORCE_REAL_RETURN_REASONS,
    });

    // Retrieve the server side cached init response data
    // Is generated from getServerSideProps
    const initServiceData = queryClient.getQueryData([SERVER_SIDE_CACHE_KEYS.INIT, brandName, serverHostKey, includeReturnReasons]);
    const fromUrlBrandData = initServiceData?.data;

    // Helping navigate function to pre-populate brand in the query
    const navigate = useCallback(
        (url, as, options) => {
            if (_.isObject(url)) {
                return router.push(
                    {
                        ...url,
                        query: { brand: router.query.brand, ...url.query },
                    },
                    as,
                    options,
                );
            } else {
                return router.push(url, as, options);
            }
        },
        [router],
    );

    const [brandData, setBrandData] = useState(null);
    const [mockedServices, setMockedServices, mockedServicesInitialized] = useBrowserSessionAndLocalState({
        key: QUERY_AND_SESSION_KEYS.KEY_MOCKED_SERVICES,
        defaultValue: false,
        browserStorageType: BROWSER_STORAGE_TYPES.BOTH,
    });
    const [host, setHost, hostInitialized] = useBrowserStateWithCookieCheck({
        key: QUERY_AND_SESSION_KEYS.HOST,
        defaultValue: ENVIRONMENTS.PROD,
        browserStorageType: BROWSER_STORAGE_TYPES.BOTH,
        includeQuery: true,
    });
    const [mockTimer, setMockTimer, mockTimerInitialized] = useBrowserStateWithCookieCheck({
        defaultValue: 500,
        key: QUERY_AND_SESSION_KEYS.MOCK_TIMER,
        browserStorageType: BROWSER_STORAGE_TYPES.BOTH,
        includeQuery: true,
    });

    useEffect(() => {
        if (!brandName && router.isReady) {
            router.push({
                pathname: "/demo-brand",
                query: router.query,
            });
        }
    }, [brandName, router.isReady]);

    const [initializedBrowserData, setInitializedBrowserData] = useState(null);

    useEffect(() => {
        if (mockedServicesInitialized && hostInitialized && mockTimerInitialized) {
            setInitializedBrowserData(true);
        }
    }, [mockedServicesInitialized, hostInitialized, mockTimerInitialized]);

    useEffect(() => {
        if (initializedBrowserData && !brandData) {
            if (mockedServices) {
                setBrandData(getLocalMockBrandData(brandName));
            } else {
                setBrandData(fromUrlBrandData);
            }
        }
    }, [initializedBrowserData, brandData, brandName, fromUrlBrandData]);

    const modifiedSystem = {
        ...originalSystem,
        host: HOSTS[host]?.baseUrl,
        fetch: (...args) => {
            return devFetch(
                {
                    productsBundleMock: brandData?.productsBundleMock,
                    system: originalSystem,
                    brandName: brandName,
                    mockedServices: isBrandLocalMock(brandName) || mockedServices,
                    mockTimer,
                    brandData,
                    forceRealReturnReasons,
                },
                ...args,
            );
        },
    };

    const [selectedIframeOption, setSelectedIframeOption] = useQueryState({
        key: QUERY_AND_SESSION_KEYS.IFRAME_OPTION,
    });

    const iframeActive = selectedIframeOption && selectedIframeOption !== IFRAME_OPTIONS.NO_IFRAME.value;

    const globalIframeModalActive = selectedIframeOption === IFRAME_OPTIONS.TWO_IFRAMES.value;

    const { serviceData: brandNamesServiceData, fetchServiceData: brandNamesFetch } = useServiceHook({
        system: modifiedSystem,
        fetchRequested: false,
        fetchFn: fetchBrandNames,
    });

    useEffect(() => {
        if (host) {
            brandNamesFetch();
        }
    }, [host]);

    useEffect(() => {
        setBrandData(getLocalMockBrandData(brandName) || fromUrlBrandData);
    }, [brandName]);

    const listenerFunction = useCallback((event) => {
        const mainIframe = window.document.getElementById(INGRID_IFRAME_ID);
        const modalIframe = window.document.getElementById(INGRID_IFRAME_MODAL_APP_ID);

        const isFromMainIframe = event.source === mainIframe?.contentWindow;
        const isFromModalIframe = event.source === modalIframe?.contentWindow;

        if (event?.data?.relayToOtherIframe) {
            // Just forward the messages between each other
            const toIframe = isFromMainIframe ? modalIframe : mainIframe;
            toIframe?.contentWindow?.postMessage(event?.data);
            return;
        }

        if (isFromMainIframe && event?.data?.action === IFRAME_ACTIONS.APP_SIZE_CHANGE) {
            mainIframe.style.height = `${event?.data?.height}px`;
        }

        if (isFromMainIframe && event?.data?.action === IFRAME_ACTIONS.PAGE_CHANGE && event?.data?.page !== "LOGIN") {
            if (mainIframe.getBoundingClientRect().bottom > window.innerHeight) {
                mainIframe.scrollIntoView(false);
            }

            if (mainIframe.getBoundingClientRect().top < 0) {
                mainIframe.scrollIntoView();
            }
        }

        if (isFromModalIframe && event?.data?.action === IFRAME_ACTIONS.OPEN_MODAL) {
            modalIframe.style.width = "100vw";
            modalIframe.style.height = "100vh";
            modalIframe.style.visibility = "initial";
        }

        if (isFromModalIframe && event?.data?.action === IFRAME_ACTIONS.CLOSE_MODAL) {
            modalIframe.style.width = "0";
            modalIframe.style.height = "0";
            modalIframe.style.visibility = "hidden";
        }
    }, []);

    useEffect(() => {
        window.addEventListener("message", listenerFunction);
        return () => {
            window.removeEventListener("message", listenerFunction);
        };
    }, [listenerFunction]);

    useEffect(() => {}, []);

    const [iframeLanguage, setIframeLanguage] = useState("");

    const onIframeLanguageEnter = useCallback((v) => {
        const iframe = window.document.getElementById(INGRID_IFRAME_ID);
        iframe?.contentWindow?.postMessage({
            action: IFRAME_ACTIONS.SET_LANGUAGE_CODE,
            data: {
                languageCode: v,
            },
        });
    }, []);

    // const isModalIframeApp = router.pathname.startsWith(ROUTES.IFRAME_MODAL_APP.pathname);
    // if (isModalIframeApp) {
    //     return (
    //         <AppEngine
    //             Page={Page}
    //             brandName={brandName}
    //             brandData={brandData}
    //             router={router}
    //             navigate={navigate}
    //             system={modifiedSystem}
    //             initServiceData={{
    //                 ...initServiceData,
    //                 data: brandData,
    //                 ...(brandData && { status: 200, error: null }),
    //             }}
    //         />
    //     );
    // }

    return (
        <>
            {brandData && (
                <SettingsComponent
                    brandName={brandName}
                    brandData={brandData}
                    setBrandData={setBrandData}
                    additionalSettings={
                        <>
                            <TurnrDropdownSelect fullWidth name="host" brandData={brandData} options={getHostSelectOptions()} value={host} onChange={setHost} label="Host" />
                            <TurnrDropdownSelect
                                label="brand"
                                fullWidth
                                disabled={serviceIsLoading(brandNamesServiceData)}
                                loading={serviceIsLoading(brandNamesServiceData)}
                                brandData={brandData}
                                value={brandName}
                                onChange={(value) => {
                                    navigate({
                                        pathname: ROUTES.MAIN_BRAND_PATH.pathname,
                                        query: {
                                            brand: value || devBrandData().brandName,
                                            [QUERY_AND_SESSION_KEYS.HOST]: host,
                                        },
                                    });
                                }}
                                placeholder="Brand"
                                options={getLocalMockBrandNames().concat(_.sortBy(brandNamesServiceData.data?.brandNames || []))}
                            />
                            <div className={styles["brand-wrapper"]}>
                                <div
                                    style={{
                                        display: "flex",

                                        alignItems: "center",
                                    }}
                                >
                                    <Input
                                        name="mockTimer"
                                        value={mockTimer}
                                        disabled={!mockedServices && !isBrandLocalMock(brandName)}
                                        type="number"
                                        width={"100%"}
                                        onChange={setMockTimer}
                                        placeholder="Mock service delay (ms)"
                                    />
                                </div>
                                <div
                                    style={{
                                        display: "flex",
                                        marginLeft: "10px",
                                        alignItems: "center",
                                    }}
                                >
                                    <span style={{ marginRight: "10px" }}>Mocked?</span>
                                    <Checkbox onChange={() => setMockedServices(!mockedServices)} checked={isBrandLocalMock(brandName) || mockedServices} />
                                </div>
                            </div>
                            <TurnrDropdownSelect
                                label="App Environment"
                                fullWidth
                                brandData={brandData}
                                value={APPS.DEV_APP}
                                onChange={setAppEnvironment}
                                placeholder="App Environment"
                                options={APP_ENVIRONMENT_OPTIONS}
                            />
                            <TurnrDropdownSelect
                                fullWidth
                                brandData={brandData}
                                value={selectedIframeOption}
                                onChange={setSelectedIframeOption}
                                label="Iframe"
                                options={Object.values(IFRAME_OPTIONS)}
                            />
                        </>
                    }
                />
            )}
            {iframeActive && (
                <div
                    style={{
                        height: "100vh",
                        width: "100vw",
                        margin: "auto",
                        backgroundColor: "lightGray",
                        // display: "flex",
                        // gap: "40px",
                        justifyContent: "center",
                    }}
                >
                    <div>
                        <iframe
                            id={INGRID_IFRAME_ID}
                            src={window.location.origin + window.location.pathname + (globalIframeModalActive ? `?${QUERY_AND_SESSION_KEYS.MODAL_IFRAME_ACTIVE}=true` : "")}
                            style={{
                                border: "none",
                                width: "600px",
                                height: "500px",
                            }}
                        />
                    </div>
                    {globalIframeModalActive && (
                        <iframe
                            id={INGRID_IFRAME_MODAL_APP_ID}
                            src={window.location.origin + ROUTES.IFRAME_MODAL_APP.pathname.replace("[brand]", brandName)}
                            style={{
                                position: "fixed",
                                top: 0,
                                left: 0,
                                width: "0",
                                height: "0",
                                border: "none",
                                // width: "600px",
                                // height: "500px",
                            }}
                        ></iframe>
                    )}
                    <div style={{ maxWidth: "250px", marginTop: "20px" }}>
                        <Input placeholder={"Parent Language Code"} value={iframeLanguage} onChange={setIframeLanguage} onEnter={onIframeLanguageEnter} />
                    </div>
                </div>
            )}
            {brandName && brandData && (
                <div style={{ display: iframeActive && "none" }}>
                    <AppEngine
                        key={brandName}
                        Page={Page}
                        brandName={brandName}
                        brandData={brandData}
                        router={router}
                        navigate={navigate}
                        system={modifiedSystem}
                        initServiceData={{
                            ...initServiceData,
                            data: brandData,
                            ...(brandData && { status: 200, error: null }),
                        }}
                    />
                </div>
            )}
            {hostInitialized && (
                <EnvironmentSelector value={host} onChange={setHost}>
                    {(dropdownProps) => <TurnrDropdownSelect {...dropdownProps} />}
                </EnvironmentSelector>
            )}
        </>
    );
}
